// src/pages/business/services/ServiceList.tsx
// Mimic the functionality and styling of mobile ServicesScreen.tsx with web UI consistency
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Plus, RefreshCw, Wrench, Video, Loader2, ArrowLeft } from 'lucide-react';
import apiClient from '@/services/apiClient';
import { getImageUrl } from '@/config/media';
import { ThemeToggle } from '@/components/common/ThemeToggle';

const StarRating: React.FC<{ rating: number; size?: number }> = ({ rating, size = 12 }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(s => (
      <Star
        key={s}
        size={size}
        fill={s <= rating ? '#FFD700' : 'none'}
        stroke={s <= rating ? '#FFD700' : '#CBD5E1'}
      />
    ))}
    <span className="text-xs font-semibold ml-1 text-light-subtle dark:text-dark-subtle">
      {rating.toFixed(1)}
    </span>
  </div>
);

export const ServiceList: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      const res = await apiClient.get(`/businesses/services/${businessId}`);
      setServices(res.data || []);
    } catch (e) {
      console.error('Fetch services error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [businessId]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const onRefresh = () => { setRefreshing(true); fetchServices(); };

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(businessId ? `/business/${businessId}` : '/business')}
              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <ArrowLeft size={24} className="text-primary dark:text-golden" />
            </button>
            <h1 className="text-lg font-bold text-light-text dark:text-dark-text">Manage Services</h1>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate(`/business/${businessId}/reviews`)} 
              className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              title="Business Reviews"
            >
              <Star size={20} className="text-primary dark:text-golden" />
            </button>
            <ThemeToggle />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto pb-24">
        {/* Refresh Button */}
        <div className="flex justify-end px-4 pt-4 pb-1">
          <button 
            onClick={onRefresh} 
            disabled={refreshing} 
            className="flex items-center gap-1.5 text-xs text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text transition-colors"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 size={32} className="animate-spin text-primary dark:text-golden" />
          </div>
        )}

        {/* Empty State */}
        {!loading && services.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 px-10 text-center gap-4">
            <Wrench size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-lg text-light-subtle dark:text-dark-subtle">No services added yet.</p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle opacity-70">
              Create your first service to start receiving bookings and reviews
            </p>
          </div>
        )}

        {/* Services List */}
        {!loading && services.length > 0 && (
          <div className="px-4 pt-4 space-y-3">
            {services.map(item => {
              const firstMedia = item.media_urls?.items?.[0]?.url || null;
              const hasVideo = item.media_urls?.items?.some((m: any) => m.type === 'video');
              const tags = item.tags
                ? Array.isArray(item.tags) ? item.tags : item.tags.split(',').map((t: string) => t.trim())
                : [];

              return (
                <button
                  key={item.id}
                  onClick={() => navigate(`/business/${businessId}/services/${item.id}`)}
                  className="w-full flex items-start gap-4 p-4 rounded-xl border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card shadow-sm hover:shadow-md transition-all text-left relative group"
                >
                  {/* Media — 70x70 */}
                  {firstMedia ? (
                    <div className="relative flex-shrink-0 rounded-lg overflow-hidden" style={{ width: 70, height: 70 }}>
                      <img src={getImageUrl(firstMedia)} alt={item.name} className="w-full h-full object-cover" />
                      {hasVideo && (
                        <div className="absolute top-1 right-1 rounded-lg p-0.5 bg-black/60">
                          <Video size={16} className="text-white" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex-shrink-0 flex items-center justify-center rounded-lg w-[70px] h-[70px] bg-light-bg dark:bg-dark-bg">
                      <Wrench size={24} className="text-light-subtle dark:text-dark-subtle" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    {/* Name */}
                    <p className="font-semibold text-sm truncate text-light-text dark:text-dark-text mb-1">
                      {item.name}
                    </p>

                    {/* Rating */}
                    {item.rating > 0 && (
                      <div className="flex items-center gap-2 mb-2">
                        <StarRating rating={item.rating} size={11} />
                        <span className="text-xs text-light-subtle dark:text-dark-subtle">({item.reviews_count || 0})</span>
                      </div>
                    )}

                    {/* Price & Duration */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-bold text-sm text-primary dark:text-golden">
                        KES {item.price?.toLocaleString()}
                      </span>
                      {item.duration_minutes && (
                        <span className="text-xs text-light-subtle dark:text-dark-subtle">{item.duration_minutes} min</span>
                      )}
                    </div>

                    {/* Category */}
                    {item.category && (
                      <p className="text-xs text-sky-500 dark:text-sky-400 mb-1.5">{item.category}</p>
                    )}

                    {/* Tags */}
                    {tags.length > 0 && (
                      <div className="flex gap-1.5 mb-2 flex-wrap">
                        {tags.slice(0, 2).map((tag: string, i: number) => (
                          <span 
                            key={i} 
                            className="px-2 py-1 rounded-md text-[11px] font-medium bg-primary/10 dark:bg-primary/20 text-primary dark:text-golden"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Status */}
                    <div className="flex items-center gap-2">
                      {item.is_featured && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-300 dark:bg-yellow-400 text-black">
                          <Star size={12} fill="currentColor" />
                          Featured
                        </span>
                      )}
                      <span className="text-xs text-light-subtle dark:text-dark-subtle">
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Reviews Button */}
                  {item.reviews_count > 0 && (
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/business/${businessId}/reviews`); }}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary dark:bg-golden text-white dark:text-black text-xs font-semibold flex-shrink-0"
                    >
                      <Star size={13} fill="currentColor" />
                      Reviews
                    </button>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* FAB — Create New Service */}
      <button
        onClick={() => navigate(`/business/${businessId}/services/new`)}
        className="fixed bottom-8 right-8 flex items-center justify-center rounded-full shadow-lg z-50 bg-primary dark:bg-golden hover:shadow-xl transition-shadow"
        style={{ width: 56, height: 56 }}
        title="Create new service"
      >
        <Plus size={28} className="text-white dark:text-black" />
      </button>
    </div>
  );
};

export default ServiceList;
